import "./style.scss";

import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelData, ChannelParams, ITermFormData } from "containers/TermFormContainer";
import { showsDefaultChannelConfiguration } from "containers/TermFormContainer/utils";
import { useTranslation } from "react-i18next";
import type { ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import ChannelFormatOverrides from "../../ChannelPercentages/ChannelFormatOverrides";
import { channelUsesSalesBaseColumns } from "../../ChannelBaseColumnsFields";
import { resolveChannelMilestoneBaseColumn } from "../../milestoneBaseColumnUtils";
import MilestonesInput from "../../MilestonesInput";
import ShowDefaultConfigurationField from "../../ShowDefaultConfigurationField";
import type { GroupWithChannels } from "../../types";

interface IGroupPercentages {
    /**
     * To display number of channel (mainly visual)
     */
    index: number;
    /**
     * Channel displayed
     */
    channel: IChannel;
    /**
     * Whether the group is currently active.
     */
    currentlyActive: boolean;
    /**
     * Handles changes of percentages and tiers.
     */
    handleChannelUpdates: (newChannel: any, groupId?: number) => void;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    mode: "edit" | "new";
    /**
     * Current channel default formula (formatTypeId null)
     */
    existingChannel: ChannelParams | undefined;
    chosenChannels: ChannelParams[];
    formatTypesOnWork: { id: number; name: string }[];
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    /**
     * Current group
     */
    group: GroupWithChannels;
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    from?: "templates" | "form";
    onShowDefaultConfigurationChange?: (channelId: number, showDefaultConfiguration: boolean) => void;
}

/**
 * Unsynced group channel: milestone ladder + optional format overrides.
 * Calculation / milestone bases stay on the parent GroupSection (group-scoped).
 */
const GroupPercentages = ({
    index,
    channel,
    currentlyActive,
    handleChannelUpdates,
    handleToggleFormatTypeOverride,
    mode,
    existingChannel,
    chosenChannels,
    formatTypesOnWork,
    calculationBaseColumnLabels,
    group,
    useFormData,
    from = "form",
    onShowDefaultConfigurationChange,
}: IGroupPercentages) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;
    const showDefaultConfiguration = showsDefaultChannelConfiguration(existingChannel);
    const isProportional = channel.calculationMethod === CalculationMethod.PROPORTIONAL;
    const showProportionalLadders = currentlyActive && channelUsesSalesBaseColumns(channel) && isProportional;
    const showDefaultToggle = from === "templates" && isProportional;

    return (
        <div className="channel-line" key={`channel-${channel.id}`}>
            <div className="channel-top">
                <div className="channel-title">
                    <span id={`channel-${channel.id}-title`}>
                        {index}. {channel.name}
                    </span>
                </div>
                <div className="switch" id={`switch-${channel.id}`}>
                    {currentlyActive && channel.calculationMethod === CalculationMethod.PACKAGE && (
                        <div className="channel-reminder channel-activated">
                            <div className="chip channel-activated" />
                            {t("ConnectTitle.ChannelActivated")}
                        </div>
                    )}
                </div>
            </div>
            {showProportionalLadders && (
                <div className="channel-config">
                    {showDefaultToggle && (
                        <ShowDefaultConfigurationField
                            checked={showDefaultConfiguration}
                            onChange={(showDefault) => onShowDefaultConfigurationChange?.(channel.id, showDefault)}
                        />
                    )}
                    {showDefaultConfiguration && (
                        <div className="channel-milestones">
                            <MilestonesInput
                                hasMilestones={!!channel.hasMilestones}
                                channel={channel}
                                milestoneBaseColumn={resolveChannelMilestoneBaseColumn(channel, existingChannel)}
                                onChange={(newChannel: any) =>
                                    handleChannelUpdates({ ...newChannel, formatTypeId: null }, group.id)
                                }
                                propMilestones={existingChannel?.data as ChannelData[]}
                                mode={mode}
                                useFormData={{ setError, clearErrors }}
                            />
                        </div>
                    )}
                    <ChannelFormatOverrides
                        channel={channel}
                        formatTypesOnWork={formatTypesOnWork}
                        chosenChannels={chosenChannels}
                        calculationBaseColumnLabels={calculationBaseColumnLabels}
                        mode={mode}
                        useFormData={{ setError, clearErrors }}
                        handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                        handleChannelUpdates={(newChannel) => handleChannelUpdates(newChannel, group.id)}
                        hideBaseColumns
                        allowCompensationGroup
                    />
                </div>
            )}
        </div>
    );
};

export default GroupPercentages;
